import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, User, Loader2 } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';

interface EditNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFirstName: string | null;
  currentLastName: string | null;
  currentDisplayName: string | null;
  onSave: (firstName: string, lastName: string) => void;
}

export function EditNameModal({
  isOpen,
  onClose,
  currentFirstName,
  currentLastName,
  currentDisplayName,
  onSave,
}: EditNameModalProps) {
  const { t } = useTranslation(['profile', 'common']);
  const { user } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize values when modal opens
  useEffect(() => {
    if (isOpen) {
      // Use first_name/last_name if available, otherwise parse display_name
      if (currentFirstName) {
        setFirstName(currentFirstName);
        setLastName(currentLastName || '');
      } else if (currentDisplayName) {
        // Parse display_name into first and last name
        const parts = currentDisplayName.trim().split(' ');
        setFirstName(parts[0] || '');
        setLastName(parts.slice(1).join(' ') || '');
      } else {
        setFirstName('');
        setLastName('');
      }
      setError(null);
    }
  }, [isOpen, currentFirstName, currentLastName, currentDisplayName]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!user) return;

    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();

    if (!trimmedFirstName) {
      setError(t('profile:modals.editName.firstNameRequired'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const displayName = trimmedLastName
        ? `${trimmedFirstName} ${trimmedLastName}`
        : trimmedFirstName;

      // Update profile in database
      const { error: dbError } = await supabase
        .from('profiles')
        .update({
          first_name: trimmedFirstName,
          last_name: trimmedLastName || null,
          display_name: displayName,
        })
        .eq('id', user.id);

      if (dbError) throw dbError;

      // Also update user metadata
      await supabase.auth.updateUser({
        data: { display_name: displayName },
      });

      onSave(trimmedFirstName, trimmedLastName);
      onClose();
    } catch (err) {
      console.error('Error saving name:', err);
      setError(t('profile:modals.editName.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-pp-orange" />
              </div>
              <h2>{t('profile:modals.editName.title')}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4">
            <p className="text-sm text-white/70">
              {t('profile:modals.editName.description')}
            </p>

            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-[10px] text-sm text-red-300">
                {error}
              </div>
            )}

            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                {t('profile:modals.editName.firstName')} *
              </label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t('profile:modals.editName.firstNamePlaceholder')}
                maxLength={50}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                {t('profile:modals.editName.lastName')}
              </label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t('profile:modals.editName.lastNamePlaceholder')}
                maxLength={50}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t('common:actions.save')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
